import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PhoneSheet } from '../chats/phone/PhoneSheet'
import { FeatherIcon } from '../components/FeatherIcon'
import { useAccounts } from '../context/AccountsContext'
import { useMobileMenu } from '../context/SidebarContext'
import type { CrmAccount } from './crmAccess'
import { useCrmHost } from './CrmHostContext'
import { useUnreadChats } from '../components/unreadChats'
import { SearchClearButton } from '../components/SearchClearButton'
import { useAccountSearch } from '../hooks/useAccountSearch'
import './CrmAccountSheet.css'

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const nameOf = (a: CrmAccount) => a.profile_name || a.username || a.account_id

/** An Account's picture (a public URL, as the sidebar shows it), or its initials on the design's rounded square. */
export function AccountPic({ name, url, className }: { name: string; url?: string | null; className: string }) {
  const [broken, setBroken] = useState(false)
  return <span className={className}>{url && !broken ? <img src={url} alt="" onError={() => setBroken(true)} /> : initialsOf(name)}</span>
}

/** Whether OnlyFans says the Account's login is dead (this session's gateway answers, or the console's list). */
function useLoggedOut() {
  const crm = useCrmHost()
  const { accounts } = useAccounts()
  return useMemo(() => {
    const console = new Map(accounts.map((a) => [a.account_id, a]))
    return (id: string) => crm.isLoggedOut(id) || console.get(id)?.is_logged_in === false
  }, [crm, accounts])
}

/** Class names of the tab bar the corner sits in: the CRM bar's, or the shell's. */
export interface AccountTabClasses {
  tab: string
  pill: string
  label: string
  badge: string
}

const CRM_TAB: AccountTabClasses = { tab: 'crm-mtab', pill: 'crm-mtab-pill', label: 'crm-mtab-label', badge: 'crm-rail-badge crm-mtab-badge' }

/**
 * The phone tab bar's last tab (ADR 0035, the design's accounts corner): the
 * open Account's picture and name, or, on console pages with no Account open,
 * "Accounts". A badge counts the other Accounts with unread chats (OnlyFans'
 * count, ADR 0037); a dot says one of them is logged out.
 */
export function CrmAccountTab({ accountId, page, classes = CRM_TAB }: { accountId?: string; page?: string; classes?: AccountTabClasses }) {
  const { t } = useTranslation('library')
  const crm = useCrmHost()
  const unreadChats = useUnreadChats()
  const loggedOut = useLoggedOut()
  const [open, setOpen] = useState(false)
  const account = accountId ? crm.crmAccounts.find((a) => a.account_id === accountId) : undefined
  const othersUnread = Object.entries(unreadChats).reduce((sum, [id, n]) => (id === accountId ? sum : sum + (n > 0 ? 1 : 0)), 0)
  const anyLoggedOut = crm.crmAccounts.some((a) => loggedOut(a.account_id))
  const label = account ? nameOf(account).split(/\s+/)[0] : t('crmRail.accounts')
  return (
    <>
      <button type="button" className={`${classes.tab} crm-mtab-account ${open ? 'is-on' : ''}`} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <span className={classes.pill}>
          {account ? <AccountPic name={nameOf(account)} url={account.avatar_url} className="crm-mtab-acc-pic" /> : <FeatherIcon name="layers" size={21} />}
          {othersUnread > 0 ? (
            <span className={classes.badge} aria-label={t('crmRail.otherUnread', { count: othersUnread })}>
              {othersUnread > 99 ? '99+' : othersUnread}
            </span>
          ) : anyLoggedOut ? (
            <span className="crm-mtab-acc-dot" role="img" aria-label={t('crmRail.someLoggedOut')} />
          ) : null}
        </span>
        <span className={classes.label} translate="no">
          {label}
        </span>
      </button>
      <CrmAccountSheet open={open} onClose={() => setOpen(false)} accountId={accountId} page={page} />
    </>
  )
}

interface SheetProps {
  open: boolean
  onClose: () => void
  accountId?: string
  /** The CRM page the person is on: picking an Account opens the same page there (else its Chats). */
  page?: string
}

/**
 * Every Account this login can open in the CRM (search, unread when known,
 * logged-out ones flagged), Workspace on top in the design's "All accounts"
 * place, and the full menu drawer at the bottom.
 */
export function CrmAccountSheet({ open, onClose, accountId, page }: SheetProps) {
  const { t } = useTranslation('library')
  const crm = useCrmHost()
  const unreadChats = useUnreadChats()
  const navigate = useNavigate()
  const menu = useMobileMenu()
  const loggedOut = useLoggedOut()
  // The Accounts list's search: kept when an Account is picked, so reopening the
  // sheet finds the list as it was left. It applies only while the box is shown.
  const [query, setQuery] = useAccountSearch()
  const searchable = crm.crmAccounts.length > 4
  const q = searchable ? query.trim().toLowerCase() : ''
  const accounts = useMemo(() => {
    const unread = (a: CrmAccount) => unreadChats[a.account_id] ?? 0
    return crm.crmAccounts
      .filter((a) => !q || nameOf(a).toLowerCase().includes(q) || a.username.toLowerCase().includes(q))
      .sort((a, b) => unread(b) - unread(a) || nameOf(a).localeCompare(nameOf(b)))
  }, [crm.crmAccounts, unreadChats, q])
  const go = (to: string) => {
    onClose()
    navigate(to)
  }

  return (
    <PhoneSheet open={open} onClose={onClose} label={t('crmRail.accounts')} title={t('crmRail.accounts')} aside={t('crmRail.connected', { count: crm.crmAccounts.length })}>
      <div className="crm-acc-sheet">
        {searchable && (
          <span className="ph-field crm-acc-search">
            <FeatherIcon name="search" size={16} />
            <input type="search" value={query} placeholder={t('crmRail.searchAccounts')} aria-label={t('crmRail.searchAccounts')} autoComplete="off" onChange={(e) => setQuery(e.target.value)} />
            <SearchClearButton value={query} onClear={() => setQuery('')} size={16} />
          </span>
        )}
        <div className="crm-acc-list">
          {!q && (
            <button type="button" className="crm-acc-row" onClick={() => go('/workspace')}>
              <span className="crm-acc-pic is-icon">
                <FeatherIcon name="layers" size={20} />
              </span>
              <span className="crm-acc-main">
                <span className="crm-acc-name">{t('crmRail.workspace')}</span>
                <span className="crm-acc-sub">{t('crmRail.workspaceSub')}</span>
              </span>
            </button>
          )}
          {accounts.map((a) => {
            const on = !!page && a.account_id === accountId
            const dead = loggedOut(a.account_id)
            const unread = unreadChats[a.account_id] ?? 0
            const sub = dead
              ? t('crmRail.loginExpired')
              : [a.username && `@${a.username}`, unread > 0 && t('crmRail.unread', { count: unread })].filter(Boolean).join(' · ')
            return (
              <button
                key={a.account_id}
                type="button"
                className={`crm-acc-row ${on ? 'is-on' : ''} ${dead ? 'is-dead' : ''}`}
                aria-current={on ? 'page' : undefined}
                onClick={() => (on ? onClose() : go(`/account/${encodeURIComponent(a.account_id)}/crm/${page ?? 'chats'}`))}
              >
                <AccountPic name={nameOf(a)} url={a.avatar_url} className="crm-acc-pic" />
                <span className="crm-acc-main">
                  <span className="crm-acc-name" translate="no">
                    {nameOf(a)}
                  </span>
                  <span className="crm-acc-sub">{sub}</span>
                </span>
                {unread > 0 && <span className="crm-acc-unread">{unread > 99 ? '99+' : unread}</span>}
                {on && <FeatherIcon name="check" size={17} />}
              </button>
            )
          })}
          {q && !accounts.length && <span className="crm-acc-none">{t('crmRail.noAccountsMatch', { query: query.trim() })}</span>}
        </div>
        <button
          type="button"
          className="crm-acc-menu"
          onClick={() => {
            onClose()
            menu.setMobileMenuOpen(true)
          }}
        >
          <FeatherIcon name="menu" size={17} />
          {t('crmRail.fullMenu')}
        </button>
      </div>
    </PhoneSheet>
  )
}
